import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useAppStore } from '../../../app/api/app-api';
import { useAppOwners } from '../../../application-owners/api/applications-owners-api';
import ApplicationIrrelevant from '../../../applications/components/ApplicationIrrelevant';
import AppSpinner from '../../../common/components/AppSpinner';
import EmptyPageAvatar from '../../../common/components/EmptyPageAvatar';
import SidebarLayout from '../../../elements/SidebarLayout/SidebarLayout';
import VerticalSplitPane from '../../../elements/VerticalSplitPane/VerticalSplitPane';
import {
  useGradualUpdate,
  useScan,
} from '../../../new-scan/actions/scan-actions';
import { onIrrelevantApp } from '../../common/actions/issues-exclusions-actions';
import { setInitialFilters } from '../actions/pipeline-filter-issues-actions';
//import PipelineIssuesTable from '../components/PipelineIssuesTable';
import {
  clearStore,
  setApplicationIrrelevantModal,
  setExclusionAppComment,
} from '../store-actions/pipeline-issues-store-actions';
import PipelineIssuesStore from '../stores/pipeline-issues-store';
import DisablePolicyContainer from '../../common/containers/DisablePolicyContainer';
import ExclusionIssueContainer from '../../common/containers/ExclusionIssueContainer';
import PipelineIssueDrawerContainer from './PipelineIssueDrawerContainer';
import PipelineIssuesSidebarContainer from './PipelineIssuesSidebarContainer';
import {
  initIssuesPipeline,
  loadFiltersPipeline,
  loadIssuesPipeline,
  sortIssuesPipeline,
} from '../actions/pipeline-issues-actions';
import PipelineIssuesTable from '../components/PipelineIssuesTable';

const PipelineIssuesContainer: FC<
  React.PropsWithChildren<PipelineIssuesContainerProps>
> = ({ navigateToConnectors }) => {
  const {
    loading,
    issues,
    selectedIssue,
    order,
    orderBy,
    applicationIrrelevantModal,
    selectedIssueId,
  } = useSnapshot(PipelineIssuesStore);
  const [searchParams] = useSearchParams();
  const filters = searchParams.get('filters');
  // const issueId = searchParams.get('issueId');
  const limit = searchParams.get('limit');
  const unit = searchParams.get('unit');
  const { displaySelectedAppOwners } = useAppOwners();
  const { isEmptyOrg } = useAppStore();

  const { isCancelScanLoading } = useScan();

  const { inProgress } = useGradualUpdate(
    useCallback(() => {
      setInitialFilters(filters);
      loadIssuesPipeline({ update: true, cache: false });
      loadFiltersPipeline({ cache: false });
    }, [filters]),
  );

  useEffect(() => {
    setInitialFilters(filters);
    loadIssuesPipeline({ update: true });
    loadFiltersPipeline();
  }, [displaySelectedAppOwners, filters, inProgress, limit, unit]);

  useEffect(() => {
    return clearStore;
  }, []);

  useEffect(() => {
    initIssuesPipeline();
  }, []);

  const handleCloseIrrelevantApp = () => {
    setApplicationIrrelevantModal(false);
  };

  if (isCancelScanLoading) {
    return <AppSpinner text='Canceling scan. Please wait...' />;
  }

  if ((loading.isIdle() || loading.isPending()) && issues.length === 0) {
    return <AppSpinner text='Loading Pipeline Issues...' />;
  }

  if (inProgress && issues.length === 0) {
    return (
      <EmptyPageAvatar
        text={`A scan is now running. The first results will appear soon so don't MOOOOve.`}
      />
    );
  }

  if (loading.isError()) {
    return (
      <Alert severity='error'>
        <AlertTitle>Error</AlertTitle>
        <Typography variant='body1'>An error has occurred</Typography>
      </Alert>
    );
  }

  if (isEmptyOrg && !inProgress) {
    return <EmptyPageAvatar text='No pipeline issues.' />;
  }

  return (
    <SidebarLayout aside={<PipelineIssuesSidebarContainer />}>
      <Stack direction='column' height='100%' sx={{ overflowY: 'auto' }}>
        <VerticalSplitPane
          content={
            <>
              {issues.length === 0 && (
                <EmptyPageAvatar
                  text={
                    <Typography
                      variant='body1'
                      color='white'
                      textAlign='center'>
                      No issues to show!
                      {filters && (
                        <>
                          <br />
                          Try removing some filters to show more results
                        </>
                      )}
                    </Typography>
                  }
                />
              )}
              {issues.length > 0 && (
                <PipelineIssuesTable
                  issues={issues}
                  selectedID={selectedIssueId}
                  order={order[0]}
                  orderBy={orderBy[0]}
                  onSort={sortIssuesPipeline}
                  loading={loading.isPending()}
                />
              )}
            </>
          }>
          {({ className }) => (
            <PipelineIssueDrawerContainer className={className} />
          )}
        </VerticalSplitPane>
      </Stack>
      <DisablePolicyContainer />
      <ApplicationIrrelevant
        applicationIrrelevantModal={applicationIrrelevantModal}
        onIrrelevantApp={onIrrelevantApp}
        appName={selectedIssue?.app.name}
        appId={selectedIssue?.app.id}
        handleClose={handleCloseIrrelevantApp}
        excludeComment={selectedIssue?.exclusionAppComment}
        setExcludeComment={setExclusionAppComment}
      />
      <ExclusionIssueContainer />
    </SidebarLayout>
  );
};

interface PipelineIssuesContainerProps {
  navigateToConnectors(): void;
}

export default PipelineIssuesContainer;
